<template>
  <AdminLayout>
    <Head title="Orders" />

    <template #pageTitle> Orders </template>

    <template #headingOptions>
      <div class="relative inline-flex self-start h-12">
        <div class="input-group-checkbox items-center">
          <div class="input-checkbox-container">
            <input
              v-model="customFiltersForm.showReturnableOrders"
              id="show_returnable_orders"
              name="show_returnable_orders"
              type="checkbox"
            />
          </div>
          <div class="input-label-container">
            <label for="show_returnable_orders"> Returnable </label>
          </div>
        </div>

        <div class="input-group-checkbox items-center ml-4">
          <div class="input-checkbox-container">
            <input
              v-model="customFiltersForm.showReservedOrders"
              id="show_reserved_orders"
              name="show_reserved_orders"
              type="checkbox"
            />
          </div>
          <div class="input-label-container">
            <label for="show_reserved_orders"> Reserved </label>
          </div>
        </div>

        <div class="input-group-checkbox items-center ml-4">
          <div class="input-checkbox-container">
            <input
              v-model="customFiltersForm.showMissingPackages"
              id="show_missing_packages"
              name="show_missing_packages"
              type="checkbox"
            />
          </div>
          <div class="input-label-container">
            <label for="show_missing_packages"> Missing Packages </label>
          </div>
        </div>

        <Menu
          v-if="showingActionButton"
          as="div"
          class="relative ml-4 text-left"
        >
          <div>
            <MenuButton
              class="inline-flex justify-center w-full rounded-md border border-gray-400 shadow-sm px-4 py-3 bg-gray-100 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-400 focus:ring-blue-600"
            >
              Actions
              <ChevronDownIcon
                class="-mr-1 ml-2 h-5 w-5"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <MenuItems
              class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray-100 ring-1 ring-gray-400 ring-opacity-5 divide-y divide-gray-400 focus:outline-none z-50"
            >
              <div class="py-1">
                <MenuItem>
                  <a
                    @click.prevent="printSelected()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    <ArrowCircleRightIcon
                      class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                      aria-hidden="true"
                    />
                    Print Orders
                  </a>
                </MenuItem>
                <MenuItem>
                  <a
                    @click.prevent="returnSelected()"
                    href="#"
                    :class="[
                      'text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center px-4 py-2 text-sm'
                    ]"
                  >
                    <ArrowCircleRightIcon
                      class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                      aria-hidden="true"
                    />
                    Return Orders
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </template>

    <div class="w-full flex flex-col h-full overflow-y-auto">
      <div class="flex gap-1 py-2 px-4 sm:px-12">
        <div class="w-20 md:w-24">
          <SavedFiltersMenu
            :saved-filters="usersSavedFilters.data.value"
            @save="({ filterName }) => usersSavedFilters.save({ filterName })"
            @load="usersSavedFilters.load"
            @delete="usersSavedFilters.remove"
            @clear-all="resetTableToDefault"
          />
        </div>
        <div>
          <SavedFiltersReset
            :active="!stickyFilters.isInDefaultState.value"
            @clear-all="resetTableToDefault"
          />
        </div>
      </div>
      <DataGrid
        name="orders_data_table"
        :endpoint="route('api.tables.orders.index')"
        :filters="customFiltersForm"
        :columns="columns"
        :block-size="40"
        :page-size="40"
        :show-check-column="true"
      />
    </div>
  </AdminLayout>
</template>

<script setup>
import { computed, reactive, readonly, watch } from 'vue'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import { updateColumnSort } from '@/Components/DataGrid/utils'
import { useForm, Head } from '@inertiajs/vue3'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon, ArrowCircleRightIcon } from '@heroicons/vue/solid'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import formatters from '@/Mixins/formatters'
import isUrlAware from '@/Mixins/isUrlAware'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import DataGrid from '@/Components/DataGrid/Tables/Generic.vue'
import StatusCell from '@/Components/DataGrid/Cells/StatusCell.vue'
import DeliveryCell from '@/Components/DataGrid/Cells/DeliveryCell.vue'
import LinkCell from '@/Components/DataGrid/Cells/LinkCell.vue'
import DateCell from '@/Components/DataGrid/Cells/DateCell.vue'
import DollarCell from '@/Components/DataGrid/Cells/DollarCell.vue'
import { useUsersSavedFilters, useStickyFilters } from '@/Components/DataGrid/Composables/saved-filters'
import SavedFiltersMenu from '@/Components/DataGrid/Filters/SavedFiltersMenu.vue'
import SavedFiltersReset from '@/Components/DataGrid/Filters/SavedFiltersReset.vue'
import { useSelectedRowNodes } from '@/Components/DataGrid/Composables/selected-row-nodes'
import { wrapWithFlag, createFlagColumn } from '@/Components/Flags'

const SAVED_FILTERS_KEY = 'orders'

const props = defineProps({
  params: Object,
  deliveryMethods: Array,
  orderStatuses: Array,
  integrations: Array,
  locations: Array,
  deliveryStatuses: Array,
  fulfillmentStatuses: Array
})

const customFiltersInitialState = {
  showReturnableOrders: false,
  showReservedOrders: false,
  showMissingPackages: false
}

const customFiltersForm = reactive({ ...customFiltersInitialState })

const { context } = useInitGridState()

const gridApi = computed(() => context.gridApi)
const { selectedRowNodeIds, totalSelectedRowNodes } = useSelectedRowNodes({ gridApi })

const actionForm = useForm({ orders: [] })

const resetForm = () => {
  for (const key in customFiltersInitialState) {
    customFiltersForm[key] = customFiltersInitialState[key]
  }
}

const onFiltersSave = () => {
  return {
    showReturnableOrders: {
      value: customFiltersForm.showReturnableOrders
    },
    showReservedOrders: {
      value: customFiltersForm.showReservedOrders
    },
    showMissingPackages: {
      value: customFiltersForm.showMissingPackages
    }
  }
}

const onFiltersPreLoad = ({ state }) => {
  if (state.filters.custom.showReturnableOrders) {
    customFiltersForm.showReturnableOrders = state.filters.custom.showReturnableOrders.value
  }
  if (state.filters.custom.showReservedOrders) {
    customFiltersForm.showReservedOrders = state.filters.custom.showReservedOrders.value
  }
  if (state.filters.custom.showMissingPackages) {
    customFiltersForm.showMissingPackages = state.filters.custom.showMissingPackages.value
  }
}

const usersSavedFilters = useUsersSavedFilters({
  key: SAVED_FILTERS_KEY,
  gridApi: readonly(gridApi),
  onSave: onFiltersSave,
  onPreLoad: onFiltersPreLoad
})

const stickyFilters = useStickyFilters({
  key: SAVED_FILTERS_KEY,
  gridApi: readonly(gridApi),
  onSave: onFiltersSave,
  onPreLoad: onFiltersPreLoad
})

// Load the sticky filter when the grid is initialized
watch(
  gridApi,
  async () => {
    await stickyFilters.load()
  },
  { once: true }
)

// Persist the filter state to storage any time it changes.
watch(
  [() => customFiltersForm, () => context.activeFilters, () => context.activeSort],
  () => {
    stickyFilters.save()
  },
  {
    deep: true
  }
)

const resetTableToDefault = () => {
  gridApi.value.setFilterModel({})
  resetForm()
  updateColumnSort(gridApi, columns)
}

const showingActionButton = computed(() => totalSelectedRowNodes.value > 0)

const printSelected = () => {
  actionForm.orders = selectedRowNodeIds.value

  actionForm.post(route('orders.print'), {
    onFinish: () => actionForm.reset()
  })
}

const returnSelected = () => {
  actionForm.orders = selectedRowNodeIds.value

  actionForm.post(route('orders.bulk-return'), {
    onFinish: () => actionForm.reset()
  })
}

const columns = [
  {
    headerName: 'Order #',
    field: 'reference_number',
    filter: 'agTextColumnFilter',
    cellRenderer: wrapWithFlag(LinkCell),
    cellRendererParams: (params) => ({
      showFlag: Boolean(params.data.flags?.length),
      url: `/orders/${params.data.id}`,
      display: params.value
    }),
    flex: 1,
    minWidth: 160
  },
  {
    field: 'status',
    cellRenderer: StatusCell,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.orderStatuses
    }
  },
  {
    field: 'integration_name',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.integrations
    },
    suppressHeaderMenuButton: true
  },
  {
    headerName: 'Delivery Method',
    field: 'delivery_method',
    cellRenderer: DeliveryCell,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.deliveryMethods
    },
    suppressHeaderMenuButton: true
  },
  {
    headerName: 'Inv. Location',
    field: 'location_name',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.locations.map((location) => location.name)
    },
    suppressHeaderMenuButton: true
  },
  {
    headerName: 'Fulfillment Location',
    field: 'fulfillment_location_name',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.locations.map((location) => location.name)
    },
    suppressHeaderMenuButton: true
  },
  {
    headerName: 'Customer',
    field: 'customer_name',
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Total',
    field: 'total',
    filter: 'agNumberColumnFilter',
    cellRenderer: DollarCell
  },
  {
    headerName: 'Count',
    field: 'item_count',
    filter: 'agNumberColumnFilter',
    width: 150
  },
  {
    headerName: 'Fulfillment Date',
    field: 'completed_at',
    filter: 'agDateColumnFilter',
    cellRenderer: DateCell,
    cellRendererParams: {
      formatter: 'MM/DD/YYYY'
    }
  },
  {
    headerName: 'Order Date',
    field: 'created_at',
    filter: 'agDateColumnFilter',
    sort: 'desc',
    cellRenderer: DateCell,
    cellRendererParams: {
      formatter: 'MM/DD/YYYY hh:mmA'
    }
  },
  {
    headerName: 'Dispensation',
    field: 'dispensation_status',
    sortable: false
  },
  {
    headerName: 'Delivery Status',
    field: 'delivery_status',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.deliveryStatuses
    },
    suppressHeaderMenuButton: true,
    sortable: false
  },
  {
    headerName: 'Fulfillment Status',
    field: 'fulfillment_status',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.fulfillmentStatuses
    },
    suppressHeaderMenuButton: true,
    sortable: false
  },
  createFlagColumn()
]
</script>

<script>
export default {
  mixins: [formatters, isUrlAware, interactsWithCurrentLocation]
}
</script>
